import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart,
  registerables,
  ChartOptions,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  ChartData,
} from 'chart.js';

import { Box, useTheme, Typography, Paper, alpha } from '@mui/material';

import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';

Chart.register(...registerables, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

interface MonthlyData {
  month: string;
  year: string;
  result: {
    totalShipment: number;
    totalWeight: number;
    totalRevenue: number;
    totalGrossProfit: number;
  };
}

interface MixedChartProps {
  data: MonthlyData[] | undefined;
  title?: string;
}

const MixedChart: React.FC<MixedChartProps> = ({ data, title = 'Revenue, Profit & Weight Analysis' }) => {
  const theme = useTheme();

  const orgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?._country?.currency?.name || '',
  );

  // Early return if no data
  if (!data || data.length === 0) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.paper, 0.6),
          backdropFilter: 'blur(10px)',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No data available for the selected period
        </Typography>
      </Paper>
    );
  }

  const months = data.map(item => item.month);
  const weights = data.map(item => item.result.totalWeight);
  const revenues = data.map(item => item.result.totalRevenue);
  const profits = data.map(item => item.result.totalGrossProfit);

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Explicitly type the chart data to accept mixed chart types
  const chartData: ChartData<'bar', number[], string> = {
    labels: months,
    datasets: [
      {
        type: 'line' as const,
        label: 'Revenue',
        data: revenues,
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: theme.palette.primary.main,
        tension: 0.4,
        yAxisID: 'y1',
      } as any, // Type assertion needed for mixed chart types
      {
        type: 'line' as const,
        label: 'Gross Profit',
        data: profits,
        borderColor: theme.palette.secondary.main,
        backgroundColor: theme.palette.secondary.main,
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: theme.palette.secondary.main,
        tension: 0.4,
        yAxisID: 'y1',
      } as any, // Type assertion needed for mixed chart types
      {
        type: 'bar' as const,
        label: 'Weight',
        data: weights,
        backgroundColor: alpha(theme.palette.grey[500], 0.7),
        hoverBackgroundColor: theme.palette.grey[600],
        borderRadius: 8,
        barPercentage: 0.65,
        categoryPercentage: 0.8,
        yAxisID: 'y',
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 100,
    layout: {
      padding: {
        top: 20,
        right: 20,
        bottom: 10,
        left: 10,
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        grid: {
          color: alpha(theme.palette.divider, 0.1),
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
          callback: value => `${formatNumber(value as number)} kg`,
        },
        title: {
          display: true,
          text: 'Weight (kg)',
          color: theme.palette.text.secondary,
          font: {
            size: 12,
            weight: 'normal',
          },
        },
      },
      y1: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
          callback: value => formatNumber(value as number),
        },
        title: {
          display: true,
          text: `Revenue / Profit (${orgCountryCurrency})`,
          color: theme.palette.text.secondary,
          font: {
            size: 12,
            weight: 'normal',
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
          },
          maxRotation: 45,
          minRotation: 45,
        },
        title: {
          display: true,
          text: 'Month',
          color: theme.palette.text.secondary,
          font: {
            size: 12,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: context => {
            const label = context.dataset.label || '';
            const value = context.raw as number;

            if (label === 'Weight') {
              return `${label}: ${formatNumber(value)} kg`;
            }

            return `${label}: ${formatNumber(value)}`;
          },
        },
      },
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 4,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          {title}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Monthly revenue, gross profit and shipment weight comparison
        </Typography>
      </Box>

      {/* Custom Legend */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'end',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* Revenue */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: theme.palette.primary.main,
                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            />

            <Typography variant="body2" fontWeight={500} color="text.primary">
              Revenue
            </Typography>
          </Box>

          {/* Gross Profit */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: theme.palette.secondary.main,
                boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.2)}`,
              }}
            />

            <Typography variant="body2" fontWeight={500} color="text.primary">
              Gross Profit
            </Typography>
          </Box>

          {/* Weight */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: theme.palette.grey[500],
                boxShadow: `0 0 0 3px ${alpha(theme.palette.grey[500], 0.2)}`,
              }}
            />

            <Typography variant="body2" fontWeight={500} color="text.primary">
              Weight
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Chart */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'visible',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: { xs: '350px', sm: '450px', md: '500px' },
            width: '100%',
            minWidth: { xs: '300px', sm: 'auto' },
          }}
        >
          <Bar data={chartData} options={options} />
        </Box>
      </Box>
    </Paper>
  );
};

export default MixedChart;
